const STATUS_PRIORITY = [
  "on_leave",
  "holiday",
  "half_day",
  "missing_checkout",
  "missing_checkin",
  "late",
  "wfh",
  "present",
];

export const STATUS_MAP = {
  present: { label: "P", title: "Present", color: "bg-emerald-500" },
  // "No Present" dipakai (bukan "Absent") karena label ini juga menempel ke
  // hari weekend tanpa aktivitas — bukan cuma karyawan yang benar-benar bolos.
  absent: { label: "A", title: "No Present", color: "bg-red-500" },
  on_leave: { label: "L", title: "Leave", color: "bg-fuchsia-500" },
  half_day: { label: "H", title: "Half Day", color: "bg-violet-500" },
  wfh: { label: "W", title: "WFH", color: "bg-lime-500" },
  late: { label: "!", title: "Late", color: "bg-yellow-500" },
  missing_checkin: { label: "I", title: "No Checkin", color: "bg-rose-600" },
  missing_checkout: { label: "O", title: "No Checkout", color: "bg-blue-500" },
  holiday: { label: "H", title: "Holiday", color: "bg-purple-500" },
};

export const SUMMARY_ITEMS = [
  { key: "present", label: "Present", dotColor: STATUS_MAP.present.color },
  { key: "absent", label: "No Present", dotColor: STATUS_MAP.absent.color },
  { key: "on_leave", label: "Leave", dotColor: STATUS_MAP.on_leave.color },
  { key: "half_day", label: "Half Day", dotColor: STATUS_MAP.half_day.color },
  { key: "wfh", label: "WFH", dotColor: STATUS_MAP.wfh.color },
  { key: "late", label: "Late", dotColor: STATUS_MAP.late.color },
  { key: "missing_checkin", label: "No Checkin", dotColor: STATUS_MAP.missing_checkin.color },
  { key: "missing_checkout", label: "No Checkout", dotColor: STATUS_MAP.missing_checkout.color },
];

export function pad2(value) {
  return String(value).padStart(2, "0");
}

export function toLocalDateKey(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

// Payroll pakai cutoff tanggal 25: absensi tanggal 25 s/d tanggal 24 bulan
// berikutnya dihitung sebagai satu "periode". Jadi periode "Agustus" = 25 Juli
// s/d 24 Agustus. `date` yang dipilih user (mis. Agustus) dianggap sebagai
// bulan AKHIR periode.
export const CUTOFF_DAY = 25;

export function getPeriodRange(date) {
  const year = date.getFullYear();
  const month = date.getMonth();

  const start = new Date(year, month - 1, CUTOFF_DAY);
  const end = new Date(year, month, CUTOFF_DAY - 1);

  return { start, end, from: toLocalDateKey(start), to: toLocalDateKey(end) };
}

export function getAttendanceDate(record) {
  const rawDate =
    record.date ||
    record.attendance_date ||
    record.check_in ||
    record.checkin_time ||
    record.check_out ||
    record.checkout_time;

  if (!rawDate) return null;

  return String(rawDate).split("T")[0].split(" ")[0];
}

export function hasTimezone(value) {
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(String(value || ""));
}

export function formatHistoryTime(value) {
  if (!value) return "--:--";

  const raw = String(value).trim();

  if (!hasTimezone(raw)) {
    const timePart = raw.includes("T")
      ? raw.split("T")[1]
      : raw.includes(" ")
        ? raw.split(" ")[1]
        : raw;
    const [hour = "00", minute = "00"] = timePart.split(":");

    return `${pad2(hour)}:${pad2(minute)}`;
  }

  return new Date(raw).toLocaleTimeString("id-ID", {
    timeZone: "Asia/Jakarta",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

export function formatHours(hours) {
  if (hours === null || hours === undefined || hours === "") return "-";

  const numeric = Number(hours);
  if (Number.isNaN(numeric)) return String(hours);

  const hh = Math.floor(numeric);
  const mm = Math.round((numeric - hh) * 60);

  if (hh <= 0 && mm <= 0) return "0 mnt";
  if (hh <= 0) return `${mm} mnt`;
  return mm === 0 ? `${hh} jam` : `${hh} jam ${mm} mnt`;
}

export function getRecordFlags(record = {}) {
  const isWfh = Boolean(
    record.is_wfh ||
    record.pjs_is_wfh ||
    record.work_mode === "wfh" ||
    record.pjs_work_mode === "wfh" ||
    record.type === "wfh"
  );

  const isLate = Boolean(
    record.late === true ||
    Number(record.late) > 0 ||
    Number(record.late_minutes) > 0
  );

  return {
    wfh: isWfh,
    half_day: Boolean(record.is_half_day || record.status_code === "half_day"),
    missing_checkin: Boolean(
      record.is_missing_checkin ||
      record.status_code === "no_checkin" ||
      record.status_code === "missing_checkin"
    ),
    missing_checkout: Boolean(
      record.is_missing_checkout ||
      record.status_code === "no_checkout" ||
      record.status_code === "missing_checkout"
    ),
    late: isLate,
    on_leave: record.type === "leave" || record.status_code === "leave",
    holiday: record.type === "holiday",
  };
}

export function determineStatus(record = {}) {
  const flags = getRecordFlags(record);

  if (flags.on_leave) return "on_leave";
  if (flags.holiday) return "holiday";
  if (flags.half_day) return "half_day";
  if (flags.missing_checkout) return "missing_checkout";
  if (flags.missing_checkin) return "missing_checkin";
  if (flags.late) return "late";
  if (flags.wfh) return "wfh";

  return "present";
}

function mergeFlags(current = {}, incoming = {}) {
  return Object.keys({ ...current, ...incoming }).reduce((result, key) => {
    result[key] = Boolean(current[key] || incoming[key]);
    return result;
  }, {});
}

function pickStatus(currentStatus, nextStatus) {
  const currentIndex = STATUS_PRIORITY.indexOf(currentStatus);
  const nextIndex = STATUS_PRIORITY.indexOf(nextStatus);

  if (currentIndex === -1) return nextStatus;
  if (nextIndex === -1) return currentStatus;

  return nextIndex < currentIndex ? nextStatus : currentStatus;
}

export function buildAttendanceCalendar(records = [], monthDate = new Date()) {
  const dayMap = {};
  const todayStr = toLocalDateKey(new Date());
  const { start: periodStart, end: periodEnd, from, to } = getPeriodRange(monthDate);

  records.forEach((record) => {
    const dateStr = getAttendanceDate(record);
    if (!dateStr) return;
    if (dateStr < from || dateStr > to) return;

    const flags = getRecordFlags(record);
    const status = determineStatus(record);
    const current = dayMap[dateStr];

    dayMap[dateStr] = {
      status: current ? pickStatus(current.status, status) : status,
      flags: mergeFlags(current?.flags, flags),
      records: [...(current?.records || []), record],
    };
  });

  // Hari lampau tanpa record sama sekali dianggap "No Present" — TERMASUK
  // weekend (Sabtu & Minggu). Kalau memang hari libur resmi, backend akan
  // kirim record bertipe "holiday" yang di-skip terpisah di bawah, jadi
  // weekend biasa yang tidak ditandai holiday tetap konsisten dihitung.
  for (let d = new Date(periodStart); d <= periodEnd; d.setDate(d.getDate() + 1)) {
    const dateStr = toLocalDateKey(d);

    if (dateStr < todayStr && !dayMap[dateStr]) {
      dayMap[dateStr] = {
        status: "absent",
        flags: { absent: true },
        records: [],
      };
    }
  }

  const statsCounts = SUMMARY_ITEMS.reduce((result, item) => {
    result[item.key] = 0;
    return result;
  }, {});

  Object.values(dayMap).forEach((day) => {
    const flags = day.flags || {};
    const status = day.status;

    // Libur resmi: tidak dihitung sama sekali (bukan hadir, bukan absen).
    if (flags.holiday || status === "holiday") return;

    // Cuti: bukan hadir, tapi juga bukan absen — dihitung terpisah.
    if (flags.on_leave || status === "on_leave") {
      statsCounts.on_leave++;
      return;
    }

    if (status === "absent") {
      // Tidak ada record sama sekali di hari itu → benar-benar tidak hadir.
      statsCounts.absent++;
    } else if (flags.half_day || status === "half_day") {
      // Setengah hari kerja dihitung setengah kehadiran.
      statsCounts.present += 0.5;
    } else {
      // Semua kondisi lain yang tetap ada aktivitas absensi — present biasa,
      // late, WFH, atau lupa checkin/checkout — tetap dihitung HADIR PENUH.
      // Late/lupa absen bukan berarti tidak hadir, jadi tidak boleh
      // "hilang" dari total present; catatannya tetap muncul di counter
      // masing-masing di bawah sebagai info tambahan.
      statsCounts.present += 1;
    }

    if (flags.half_day || status === "half_day") statsCounts.half_day++;
    if (flags.wfh || status === "wfh") statsCounts.wfh++;
    if (flags.late || status === "late") statsCounts.late++;
    if (flags.missing_checkin || status === "missing_checkin") statsCounts.missing_checkin++;
    if (flags.missing_checkout || status === "missing_checkout") statsCounts.missing_checkout++;
  });

  return { dayMap, statsCounts };
}
